import { useState, useMemo, useEffect } from "react";
import styles from "./HistoryDialog.module.scss";
import { AppDialog } from "../AppDialog/AppDialog";
import { IconType } from "../../icons/Icon";
import { useApiQuery } from "../../infrastructure/hooks/useApiQuery";
import { IUserAuditLogItem, IHistoryConfig } from "./models/historyModels";
import { AuditTimeline } from "./AuditTimeline";
import { HistoryDetailDialog } from "./HistoryDetailDialog";
import { DataNotice } from "../dataNotice/DataNotice";

interface IHistoryCursor {
  timestamp: string;
  id: number;
}

interface ILoadedHistory {
  entityId: number;
  items: IUserAuditLogItem[];
  /** Whether the last page that arrived was full, i.e. another one probably follows. */
  hasMore: boolean;
}

interface IHistoryDialogProps {
  entityId: number;
  entityCreatedOn: string;
  timezone?: string;
  showDialog: boolean;
  onHide: () => void;
  label?: string;
  config: IHistoryConfig;
}

const SKELETON_COUNT = 4;

const HistorySkeleton = () => (
  <div className={styles.skeleton_list}>
    {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
      <div key={i} className={styles.skeleton_item}>
        <div className={styles.skeleton_item_dot} />
        {i < SKELETON_COUNT - 1 && (
          <div className={styles.skeleton_item_line} />
        )}
        <div className={styles.skeleton_item_body}>
          <div className={styles.skeleton_item_line1} />
          <div className={styles.skeleton_item_line2} />
        </div>
      </div>
    ))}
  </div>
);

export const HistoryDialog = ({
  entityId,
  entityCreatedOn,
  timezone,
  showDialog,
  onHide,
  label = "История",
  config,
}: IHistoryDialogProps) => {
  const [selectedDetailId, setSelectedDetailId] = useState<number | null>(null);
  const [cursor, setCursor] = useState<IHistoryCursor | null>(null);
  // The entity is carried alongside the rows instead of being cleared by its own
  // effect: two effects would race on mount, and clearing on close would empty the
  // list for good, because React Query answers the reopened dialog from cache with
  // the very same array instance and the merge below would never run again.
  const [loaded, setLoaded] = useState<ILoadedHistory>({
    entityId,
    items: [],
    hasMore: false,
  });

  const cursorQuery = cursor
    ? `&beforeTimestamp=${encodeURIComponent(cursor.timestamp)}&beforeId=${cursor.id}`
    : "";

  const { data: page, isLoading, isError } = useApiQuery<IUserAuditLogItem[]>({
    queryKey: [config.historyEndpoint, entityId, cursor?.id ?? 0],
    endpoint: `${config.historyEndpoint}?${config.historyQueryParam}=${entityId}${cursorQuery}`,
    enabled: showDialog && entityId > 0,
    disableErrorToast: false,
    retry: false,
    globalIsLoading: false,
  });

  // Each page is merged into the list rather than replacing it. Ids are de-duplicated
  // because a row written while the reader is paging can otherwise arrive twice, and
  // the result is re-sorted: a refetch of page one after new entries landed would
  // otherwise append today's rows below last year's.
  useEffect(() => {
    if (!page) return;

    setLoaded((previous) => {
      const base = previous.entityId === entityId ? previous.items : [];
      const seen = new Set(base.map((x) => x.id));
      const additions = page.filter((x) => !seen.has(x.id));
      const nextHasMore = !!config.pageSize && page.length === config.pageSize;

      if (
        additions.length === 0 &&
        previous.entityId === entityId &&
        previous.hasMore === nextHasMore
      ) {
        return previous;
      }

      return {
        entityId,
        items: [...base, ...additions].sort(
          (a, b) =>
            Date.parse(b.timestamp) - Date.parse(a.timestamp) || b.id - a.id,
        ),
        hasMore: nextHasMore,
      };
    });
  }, [page, entityId, config.pageSize]);

  // A failed page leaves the cursor pointing at something that will never arrive, and
  // the query key never changes again, so the reader would be stuck with no way back.
  // Step the cursor back to the start: the rows already merged are kept, and the
  // button becomes clickable again.
  useEffect(() => {
    if (isError) setCursor(null);
  }, [isError]);

  // Paging restarts for a different entity; the rows themselves are discarded by the
  // entity check inside the merge above.
  useEffect(() => {
    setCursor(null);
  }, [entityId]);

  const loadedItems = loaded.entityId === entityId ? loaded.items : [];

  const mergedHistory = useMemo(() => {
    if (loadedItems.length === 0) return null;
    return config.mergeItems ? config.mergeItems(loadedItems) : loadedItems;
  }, [loadedItems, config]);

  const showCutoffNotice = new Date(entityCreatedOn) < config.cutoffDate;
  const hasItems = !!mergedHistory && mergedHistory.length > 0;

  // Read from the last page that actually arrived, not from the in-flight query: the
  // query key changes with the cursor, so `page` is undefined for the whole fetch and
  // deciding on it would unmount the button mid-request. Merging can also shrink the
  // rendered list, which is why the raw page size is what was recorded.
  const hasMore = loaded.entityId === entityId && loaded.hasMore;

  const onLoadMore = () => {
    const oldest = loadedItems[loadedItems.length - 1];
    if (!oldest) return;

    setCursor({ timestamp: oldest.timestamp, id: oldest.id });
  };

  return (
    <>
      <AppDialog
        header={label}
        headerIconType={IconType.ClockIcon}
        headerIconColor="#6366f1"
        tooltip={config.tooltipHtml}
        visible={showDialog}
        onHide={onHide}
        style={{ maxWidth: "780px", width: "100%" }}
      >
        <div className={styles.content}>
          {/* The skeleton only stands in for the first page; later pages append
              underneath the entries already on screen. */}
          {isLoading && !hasItems && <HistorySkeleton />}

          {!isLoading && !hasItems && (
            <div className={styles.empty}>
              <span className={styles.empty_icon}>🕐</span>
              <span>Няма записана история</span>
            </div>
          )}

          {hasItems && (
            <AuditTimeline
              items={mergedHistory!}
              timezone={timezone}
              onDetailClick={(detailId) => setSelectedDetailId(detailId)}
              generateText={config.generateText}
              getCategory={config.getCategory}
              categoryColors={config.categoryColors}
            />
          )}

          {hasMore && (
            <div className={styles.load_more_wrapper}>
              <button
                className={styles.load_more}
                onClick={onLoadMore}
                disabled={isLoading}
              >
                {isLoading ? "Зареждане..." : "Покажи още"}
              </button>
            </div>
          )}

          {showCutoffNotice && (
            <div className={styles.notice_wrapper}>
              <DataNotice>
                Историята обхваща действия след{" "}
                <strong>{config.cutoffDateLabel}</strong>
              </DataNotice>
            </div>
          )}
        </div>
      </AppDialog>

      <HistoryDetailDialog
        detailId={selectedDetailId ?? 0}
        visible={selectedDetailId !== null}
        onHide={() => setSelectedDetailId(null)}
        detailEndpoint={config.detailEndpoint}
      />
    </>
  );
};
